import { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { useNavigate, useParams } from "react-router-dom";
import FormikForm, {
  FormikStep,
} from "src/components/ui-components/FormikForm";
import InputField from "src/components/ui-components/InputField";
import * as Yup from "yup";
import { Field } from "formik";
import { Done, Visibility, VisibilityOff } from "@material-ui/icons";
import { registerConfigUser, validateUserInvite } from "src/pages/ConfigUsers/ConnectionService";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from "src/components/shared/PhoneInput";
import useAuth from "src/hooks/useAuth";
import { GetpasswordPolicyMessages, GetpasswordPolicyParameter } from "src/pages/PasswordPolicy/ConnectionService";
import AlertMessage from "src/components/ui-components/AlertMessage";

type passPolicyMsg = {
  specialCharacterCricteriaMessage: string;
};

type pPolicyParam = {
  passwordPolicyKey?: string | null;
  minPasswordLength?: string;
  maxPasswordLength?: string;
  minDigitCount?: string | null;
  minUpperCaseCharCount?: string;
  minLowerCaseCharCount?: string;
  minSpecialCharCount?: string;
  allowedSpecialChars?: string;
  maxInvalidAttempts?: string;
};

const AcceptInvitation: FC = () => {
  const [passwordPolicyMessages, setPasswordPolicyMessages] = useState<string[]>([]);
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [minpassLen, setminpassLen] = useState<string>('');
  const [maxpassLen, setmaxpassLen] = useState<string>('');
  const [minDigitCount, setminDigitCount] = useState<string>('');
  const [minLowerCaseCount, setminLowerCaseCount] = useState<string>('');
  const [minUpperCaseCount, setminUpperCaseCount] = useState<string>('');
  const [minimumSpecialCharCount, setminSpecialCharCount] = useState<string>('');
  const [allowedSpecialCharacters, setallowedSpecialCharacters] = useState<string>('');
  const [isEmailCodeVerified, setIsEmailCodeVerified] =
    useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const navigate = useNavigate();
  const { userEmail, appKey } = useParams();
  const { isAuthenticated, logout } = useAuth();
  const formRef = useRef(null);

  const defaultForm = {
    FullName: "",
    Email: userEmail,
    Mobile: "",
    InviteCode: "",
    Username: "",
    Password: "",
    AppKey: appKey,
    minPasswordLength: minpassLen,
    maxPasswordLength: maxpassLen,
    minPasswordDigitCount: minDigitCount,
    minLowerCaseCharCount: minLowerCaseCount,
    minUpperCaseCharCount: minUpperCaseCount,
    minSpecialCharCount: minimumSpecialCharCount,
    allowedSpecialChars: allowedSpecialCharacters,
  };

  const submitForm = () => {
    if (formRef && formRef.current && !formRef.current.isSubmitting) {
      formRef.current.submitForm();
    }
  };

  useEffect(() => {
    GetpasswordPolicyMessages(appKey, '')
      .then((passPolicy: passPolicyMsg) => {
        if (Array.isArray(passPolicy)) {
          const policyMessages = passPolicy.map((policy) => policy.specialCharacterCricteriaMessage);
          setPasswordPolicyMessages(policyMessages);
        }
        else {
          AlertMessage({ type: "error", msg: "Invalid data format received" });
        }
      })
      .catch(() => {
        AlertMessage({ type: "error", msg: "Failed to get data" });
      });

    GetpasswordPolicyParameter(appKey)
      .then((passPolicyParam: pPolicyParam) => {
        if (passPolicyParam && passPolicyParam.minPasswordLength) {
          setminpassLen(passPolicyParam.minPasswordLength);
        }
        if (passPolicyParam && passPolicyParam.maxPasswordLength) {
          setmaxpassLen(passPolicyParam.maxPasswordLength);
        }
        if (passPolicyParam && passPolicyParam.minDigitCount) {
          setminDigitCount(passPolicyParam.minDigitCount);
        }
        if (passPolicyParam && passPolicyParam.minLowerCaseCharCount) {
          setminLowerCaseCount(passPolicyParam.minLowerCaseCharCount);
        }
        if (passPolicyParam && passPolicyParam.minUpperCaseCharCount) {
          setminUpperCaseCount(passPolicyParam.minUpperCaseCharCount);
        }
        if (passPolicyParam && passPolicyParam.minSpecialCharCount) {
          setminSpecialCharCount(passPolicyParam.minSpecialCharCount);
        }
        if (passPolicyParam && passPolicyParam.allowedSpecialChars) {
          setallowedSpecialCharacters(passPolicyParam.allowedSpecialChars);
        }
      })
      .catch(() => {
        AlertMessage({ type: "error", msg: "Failed to get data" });
      });


    if (isAuthenticated) {
      logout();
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box
        sx={{
          backgroundColor: "background.default",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="md" sx={{ py: "30px" }}>
          <Card sx={{ overflow: "visible" }}>
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textPrimary" gutterBottom variant="h5" sx={{ mb: "2px" }}>
                    Sign up to InvestApp
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <FormikForm
                    enableReinitialize
                    innerRef={formRef}
                    initialValues={defaultForm}
                    onSubmit={async (values) => {
                      setIsFormSubmitting(true);
                      const payload = {
                        AppKey: appKey,
                        Email: values.Email,
                        FullName: values.FullName,
                        Password: values.Password,
                        InviteCode: values.InviteCode,
                        Mobile: values.Mobile,
                        UserName: values.Username,
                      };
                      const res = await registerConfigUser(payload);
                      if (res === true) {
                        setIsFormSubmitting(false);
                        navigate("/authentication/login");
                      } else {
                        setIsFormSubmitting(false);
                      }
                    }}
                  >
                    <FormikStep
                      validationSchema={Yup.object({
                        FullName: Yup
                          .string()
                          .required('Full Name is required')
                          .max(100, 'Full Name must not exceed 100 characters')
                          .matches(/^\p{L}[\p{L}\s\-.']*$/u, 'Please enter valid name')
                          .test(
                            'regex',
                            'Please enter valid name',
                            (value) => {
                              return Boolean(value && value === value.trim());
                            }
                          ),
                        Password: Yup.string()
                          .test(
                            "minPAsswordLengthVal",
                            `Password must be at least ${minpassLen} characters long`,
                            function (value) {
                              const { minPasswordLength } = this.parent;
                              if (value === null || value === undefined) {
                                return true;
                              }

                              return value.length >= minPasswordLength;
                            }
                          )
                          .test(
                            "maxPasswordLengthVal",
                            "Password exceeds the maximum length policy",
                            function (value) {
                              const { maxPasswordLength } = this.parent;
                              if (value === null || value === undefined) {
                                return true;
                              }
                              return value.length <= maxPasswordLength; // Validate max length
                            }
                          )
                          .test(
                            "minDigits",
                            `Password must contain at least ${minDigitCount} digits`,
                            function (value) {
                              const { minPasswordDigitCount } = this.parent;
                              if (minPasswordDigitCount === undefined || !value) {
                                return true;
                              }
                              const digitCount = (value.match(/\d/g) || []).length;
                              return digitCount >= parseInt(minPasswordDigitCount);
                            }
                          )
                          .test(
                            "minLowerCase",
                            `Password must contain at least ${minLowerCaseCount} lowercase letter`,
                            function (value) {
                              const { minLowerCaseCharCount } = this.parent;
                              if (minLowerCaseCharCount === undefined || !value) {
                                return true;
                              }
                              const lowerCaseCount = (value.match(/[a-z]/g) || []).length;
                              return lowerCaseCount >= parseInt(minLowerCaseCharCount);
                            }
                          )
                          .test(
                            "minUpperCase",
                            `Password must contain at least ${minUpperCaseCount} uppercase letter`,
                            function (value) {
                              const { minUpperCaseCharCount } = this.parent;
                              if (minUpperCaseCharCount === undefined || !value) {
                                return true;
                              }
                              const upperCaseCount = (value.match(/[A-Z]/g) || []).length;
                              return upperCaseCount >= parseInt(minUpperCaseCharCount);
                            }
                          )
                          .test(
                            "minSpecialChars",
                            `Password must contain at least ${minimumSpecialCharCount} special character`,
                            function (value) {
                              const { minSpecialCharCount } = this.parent;
                              if (minSpecialCharCount === undefined || !value) {
                                return true;
                              }
                              const specialCharCount = (value.match(/[^A-Za-z0-9]/g) || []).length;
                              return specialCharCount >= parseInt(minSpecialCharCount);
                            }
                          )
                          .test(
                            "allowedSpecialChars",
                            `Password can only contain the following special characters: ${allowedSpecialCharacters}`,
                            function (value) {
                              const { allowedSpecialChars } = this.parent; // This is a string like "$#%"
                              if (!allowedSpecialChars || !value) return true;

                              // Regular expression to match any special character that is NOT in the allowed list
                              // We'll escape any characters in allowedSpecialChars if necessary (e.g., $ or #)
                              const regex = new RegExp(`[^A-Za-z0-9${allowedSpecialChars}]`);
                              const invalidChar = value.match(regex);  // If invalid character is found, match will return that character
                              return !invalidChar;  // Return true if no invalid characters are found
                            }
                          ),
                        InviteCode: Yup.string()
                          .required("Email Code is required")
                          .max(100, 'Email Code must not exceed 100 characters')
                          .test("regex", "Email Code not verified", () => {
                            return isEmailCodeVerified;
                          }),
                        Username: Yup.string()
                          .nullable()
                          .max(100, 'Username must not exceed 100 characters')
                          .min(5, 'Username must be at least 5 characters long')
                          .matches(/^[a-zA-z0-9]*$/u, 'Please enter a valid username')
                          .matches(/[a-zA-Z]/, 'Username should be combination of alphanumeric characters')
                          .required('Username is required'),
                        Mobile: Yup.string()
                          .required("Mobile number is required")
                          .test(
                            'phoneValidation',
                            'Must be a valid phone number',
                            (value, context) => {
                              if (!value) return true;
                              const phoneNumber = parsePhoneNumberFromString(value);
                              if (phoneNumber && phoneNumber.isValid()) {
                                return true;
                              } else {
                                return false;
                              }
                            }
                          )
                      })}
                    >
                      <Grid container spacing={1} >
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <InputField name="FullName" label="Full Name *" />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <InputField
                            name="Email"
                            label="Email *"
                            disabled
                            onChange={() => { }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <Field name="Mobile">
                            {({
                              field,
                              form: { errors, handleBlur, values, setErrors, touched },
                              meta,
                            }) => (
                              <PhoneInput
                                label="Mobile *"
                                name="Mobile"
                                {...field}
                                onBlur={handleBlur}
                                value={values.Mobile}
                                error={touched.Mobile && Boolean(meta.error)}
                                helperText={touched.Mobile && meta.error}
                                onChange={(mob) => {
                                  values.Mobile = mob || '';
                                  const phoneNumber = parsePhoneNumberFromString(values.Mobile);
                                  if (phoneNumber && phoneNumber.isValid()) {
                                    setErrors({ ...errors, Mobile: undefined });
                                  } else {
                                    setErrors({ ...errors, Mobile: 'Must be a valid phone number' });
                                  }
                                }}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <Field name="InviteCode">
                            {({ field, form, meta }) =>
                              isEmailCodeVerified ? (
                                <TextField
                                  fullWidth
                                  label="Invitation Code"
                                  disabled
                                  type="text"
                                  {...field}
                                  onChange={(InviteCode) => {
                                    form.handleChange(InviteCode);
                                  }}
                                  helperText={meta.touched && meta.error}
                                  error={Boolean(meta.touched && meta.error)}
                                  InputProps={{
                                    endAdornment: <Done color="primary" />,
                                  }}
                                />
                              ) : (
                                <TextField
                                  fullWidth
                                  label="Email Code *"
                                  type="text"
                                  {...field}
                                  onChange={(InviteCode) => {
                                    form.handleChange(InviteCode);
                                  }}
                                  helperText={meta.touched && meta.error}
                                  error={Boolean(meta.touched && meta.error)}
                                  InputProps={{
                                    endAdornment: meta.value ? (
                                      <Button
                                        sx={{ color: "#072f40" }}
                                        size="small"
                                        variant="text"
                                        type="button"
                                        onClick={async () => {
                                          form.setFieldError(
                                            "InviteCode",
                                            null
                                          );
                                          try {
                                            const isValid =
                                              await validateUserInvite(
                                                userEmail,
                                                meta.value,
                                                appKey
                                              );
                                            if (isValid) {
                                              setIsEmailCodeVerified(true);
                                            } else {
                                              form.setFieldError(
                                                "InviteCode",
                                                "Invitation Code is not valid"
                                              );
                                              return null;
                                            }
                                          } catch (error) {
                                            form.setFieldError(
                                              "InviteCode",
                                              "Invitation Code verification failed!"
                                            );
                                          }
                                          return null;
                                        }}
                                      >
                                        Verify
                                      </Button>
                                    ) : null,
                                  }}
                                />
                              )
                            }
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <InputField name="Username" label="Username *" />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <InputField
                            name="Password"
                            label="Password *"
                            type={showPassword ? "text" : "password"}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      setShowPassword((prev) => !prev);
                                    }}
                                  >
                                    {showPassword ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          {passwordPolicyMessages.length > 0 && (
                            <Box sx={{ mt: 2, ml: 1 }}>
                              <Typography variant="body2" color="textSecondary">
                                Password must meet the following criteria:
                              </Typography>
                              <ul>
                                {passwordPolicyMessages.map((message, index) => (
                                  <li key={index}>
                                    <Typography variant="body2" color="textSecondary">
                                      {message}
                                    </Typography>
                                  </li>
                                ))}
                              </ul>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </FormikStep>
                  </FormikForm>
                  <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button
                      sx={{
                        background:
                          "linear-gradient(to bottom, #072f40, #072f40)",
                        minWidth: 100,
                      }}
                      disabled={isFormSubmitting}
                      size="large"
                      type="button"
                      onClick={submitForm}
                      variant="contained"
                    >
                      Submit
                      {isFormSubmitting && (
                        <CircularProgress
                          color="primary"
                          thickness={5}
                          size={20}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default AcceptInvitation;
